import { Component, Input, SimpleChanges, Output, EventEmitter, OnChanges, Inject, LOCALE_ID } from '@angular/core';
import { User } from 'src/classes/user';
import { FormGroup, FormBuilder, Validators, FormControl } from '@angular/forms';
import { UserService } from 'src/services/user/user.service';

@Component({
  selector: 'app-email-configuration-form',
  templateUrl: './email-configuration-form.component.html',
  styleUrls: ['./email-configuration-form.component.scss']
})
export class EmailConfigurationFormComponent implements OnChanges {

  @Input() user: User;
  @Input() isEditState: boolean;
  @Input() isFormDisabled: boolean;
  @Input() isFormLoading: boolean;
  @Output() saveRequest = new EventEmitter<{value: string, key: string}[]>();
  @Output() closeRequest = new EventEmitter<void>();

  public editForm: FormGroup;
  public editFormMode: 'smtp' | 'default';
  public formErrors: string[] = [];
  public mailModeOptionLabelRecord: Record<string, string> = { 'default': 'Default E-mail Delivery System', 'smtp': 'Custom SMTP Server' };
  public securityOptionLabelRecord: Record<string, string> = { '0': 'Unencrypted', '1': 'SSL (Secure)', '2': 'TLS (Secure)' };
  public smtpTest = {
    loading: false,
    message: "",
    success: false,
  };

  constructor(
    private formBuilder: FormBuilder,
    private userService: UserService,
    @Inject(LOCALE_ID) private locale: string
  ) {
    if (!this.user) {
      this.formErrors.push('Cannot create form: User object is missing');
      return;
    }

    if (!this.isEnglishLocale()) {
      this.mailModeOptionLabelRecord = {
        'default': 'Serviço de Envio de Email Padrão',
        'smtp': 'Servidor SMTP Próprio'
      };
      this.securityOptionLabelRecord = {
        '0': 'Não criptografado',
        '1': 'SSL (Seguro)',
        '2': 'TLS (Seguro)'
      };
    }
  }

  isEnglishLocale() {
    return !(typeof this.locale === 'string' && this.locale.toLowerCase().substring(0, 2) === 'pt');
  }

  getEmailServiceLabel() {
    let mailMode = this.user.paramValues.mailMode;
    if (mailMode !== 'smtp') {
      mailMode = 'default';
    }
    if (!this.mailModeOptionLabelRecord[mailMode]) {
      return `Unknown mail mode: ${mailMode}`;
    }
    return this.mailModeOptionLabelRecord[mailMode];
  }

  getEmailEncryptionProtocol() {
    let smtpIsSafe = this.user.paramValues.smtpIsSafe;
    if (smtpIsSafe !== '1' && smtpIsSafe !== '2') {
      smtpIsSafe = '0';
    }
    if (!this.securityOptionLabelRecord[smtpIsSafe]) {
      return `Unknown smtp value: ${smtpIsSafe}`;
    }
    return this.securityOptionLabelRecord[smtpIsSafe];
  }

  setupEditForm() {
    this.formErrors = [];
    let isSMTP = !(this.user.paramValues.mailMode !== 'smtp');
    if (this.editForm) {
      isSMTP = !(this.mailMode.value !== 'smtp');
    }
    if ((isSMTP && this.editForm && this.editFormMode === 'smtp') || (!isSMTP && this.editForm && this.editFormMode === 'default')) {
      // Form is already setup at that form type
      return;
    }
    this.editFormMode = isSMTP ? 'smtp' : 'default';

    if (isSMTP) {
      this.editForm = this.formBuilder.group({
        mailMode: ['smtp', Validators.required],
        smtpHost: [this.user.paramValues.smtpHost, Validators.required],
        smtpPort: [this.user.paramValues.smtpPort || '587', Validators.required],
        smtpIsSafe: [this.user.paramValues.smtpIsSafe || '0', Validators.required],
        smtpUser: [this.user.paramValues.smtpUser, Validators.required],
        smtpPassword: [this.user.paramValues.smtpPassword],
        smtpEmailFrom: [this.user.paramValues.smtpEmailFrom, Validators.required],
      });
    } else {
      this.editForm = this.formBuilder.group({
        mailMode: ['default', Validators.required],
        mailNameLabel: [this.user.paramValues.mailNameLabel || 'ReportsFeed', Validators.required],
      });
    }

    this.mailMode.registerOnChange(this.setupEditForm.bind(this));
    this.editForm.enable();
  }

  closeEditForm() {
    this.editForm = null;
  }

  ngOnChanges(changes: SimpleChanges) {
    const isEditState = changes.isEditState;
    if (isEditState && this.user) {
      if (isEditState.currentValue && !this.editForm) {
        this.setupEditForm();
      } else if (!isEditState.currentValue && this.editForm) {
        this.closeEditForm();
      }
    }

    const isFormDisabled = changes.isFormDisabled;
    if (isFormDisabled && this.editForm) {
      if (isFormDisabled.currentValue) {
        this.editForm.disable();
      } else {
        this.editForm.enable();
      }
    }
  }

  async onTestSMTPClick() {
    if (this.smtpTest.loading) {
      return;
    }
    this.smtpTest.loading = true;
    this.smtpTest.success = false;
    this.smtpTest.message = "...";
    try {
      await this.userService.testSmtpConfig({
        smtpHost: this.editForm.get("smtpHost").value,
        smtpPort: this.editForm.get("smtpPort").value,
        smtpUser: this.editForm.get("smtpUser").value,
        smtpPassword: this.editForm.get("smtpPassword").value,
        smtpEmailFrom: this.editForm.get("smtpEmailFrom").value,
        smtpIsSafe: this.editForm.get("smtpIsSafe").value === "1" ||  this.editForm.get("smtpIsSafe").value === true ? "1" : "0",
        mailMode: "smtp",
      });
      this.smtpTest.message = "";
      this.smtpTest.success = true;
    } catch (err) {
      this.smtpTest.message = err.message;
    }
    this.smtpTest.loading = false;
  }

  onSubmitClick() {
    this.formErrors = [];
    if (this.editForm.invalid) {
      this.formErrors.push(this.isEnglishLocale() ? 'Invalid form. Review your data.' : 'Formulário inválido. Verifique seus dados.');
      this.editForm.markAllAsTouched();
      return;
    }
    try {
      const paramList: {value: string, key: string}[] = [];
      paramList.push({
        key: 'mailMode',
        value: this.mailMode.value
      });
      if (this.editFormMode === 'smtp') {
        if (this.smtpHost.dirty) { paramList.push({key: 'smtpHost', value: this.smtpHost.value}); }
        paramList.push({key: 'smtpPort', value: this.smtpPort.value});
        paramList.push({key: 'smtpIsSafe', value: this.smtpIsSafe.value});
        if (this.smtpUser.dirty) { paramList.push({key: 'smtpUser', value: this.smtpUser.value}); }
        if (this.smtpPassword.dirty) { paramList.push({key: 'smtpPassword', value: this.smtpPassword.value}); }
        if (this.smtpEmailFrom.dirty) { paramList.push({key: 'smtpEmailFrom', value: this.smtpEmailFrom.value}); }
      } else {
        if (this.mailNameLabel.dirty) { paramList.push({key: 'mailNameLabel', value: this.mailNameLabel.value}); }
      }
      this.saveRequest.emit(paramList);
    } catch (err) {
      this.formErrors = this.formErrors.concat(err.message.split('\n').filter((str: string) => !!str));
      console.log(err);
    }
  }

  onCancelClick() {
    this.closeRequest.emit();
  }

  get mailMode(): FormControl { return this.editForm.get('mailMode') as FormControl; }
  get smtpHost(): FormControl { return this.editForm.get('smtpHost') as FormControl; }
  get smtpPort(): FormControl { return this.editForm.get('smtpPort') as FormControl; }
  get smtpIsSafe(): FormControl { return this.editForm.get('smtpIsSafe') as FormControl; }
  get smtpUser(): FormControl { return this.editForm.get('smtpUser') as FormControl; }
  get smtpPassword(): FormControl { return this.editForm.get('smtpPassword') as FormControl; }
  get smtpEmailFrom(): FormControl { return this.editForm.get('smtpEmailFrom') as FormControl; }
  get mailNameLabel(): FormControl { return this.editForm.get('mailNameLabel') as FormControl; }
}
